import { useNetworkArchiveActionsArchiveContactsMutation } from '#app/__generated__/relay/useNetworkArchiveActionsArchiveContactsMutation.graphql.js';
import { useNetworkArchiveActionsArchiveMutation } from '#app/__generated__/relay/useNetworkArchiveActionsArchiveMutation.graphql.js';
import { useNetworkArchiveActionsUnarchiveContactsMutation } from '#app/__generated__/relay/useNetworkArchiveActionsUnarchiveContactsMutation.graphql.js';
import { useNetworkArchiveActionsUnarchiveMutation } from '#app/__generated__/relay/useNetworkArchiveActionsUnarchiveMutation.graphql.js';
import { useNetworkArchiveActionsUpdateMembershipsMutation } from '#app/__generated__/relay/useNetworkArchiveActionsUpdateMembershipsMutation.graphql.js';
import { useToast } from '#app/components/Toasts/hooks/useToast.js';
import { useContraMutation } from '#app/hooks/useContraMutation.js';
import { useFeatureFlag } from '#app/hooks/useFeatureFlag.js';
import { graphql } from '@contra/relay';
import { captureException } from '@contra/sentry/react';
import { useCallback } from 'react';

type SelectedContact = {
  contactId: string;
  fullName: string;
  isSavedContact: boolean;
  userProfileId: null | string;
};

type SelectedContactWithUserProfile = SelectedContact & {
  userProfileId: string;
};

export const useNetworkArchiveActions = ({
  selectedContacts,
  selectedContactsWithUserProfiles,
  skipToast = false,
}: {
  selectedContacts: SelectedContact[];
  selectedContactsWithUserProfiles: SelectedContactWithUserProfile[];
  skipToast?: boolean;
}) => {
  const toast = useToast();
  const reworkedSaveToListsFlow = useFeatureFlag('reworkedSaveToListsFlow');

  const [archiveContact] =
    useContraMutation<useNetworkArchiveActionsArchiveMutation>(graphql`
      mutation useNetworkArchiveActionsArchiveMutation(
        $input: ArchiveContactInput!
      ) {
        archiveContact(input: $input) {
          errors {
            __typename
            message
          }
          contact {
            id
            connectedUserProfile {
              isSavedContact
            }
          }
          visitor {
            userAccount {
              workspaceForSelectedUserType {
                ... on ClientWorkspace {
                  __typename
                  userLists(first: 200, filter: { groupNid: NETWORK_LIST }) {
                    edges {
                      node {
                        id
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `);

  const [unarchiveContact] =
    useContraMutation<useNetworkArchiveActionsUnarchiveMutation>(graphql`
      mutation useNetworkArchiveActionsUnarchiveMutation(
        $input: UnarchiveContactInput!
      ) {
        unarchiveContact(input: $input) {
          errors {
            __typename
            message
          }
          contact {
            id
            connectedUserProfile {
              isSavedContact
            }
          }
        }
      }
    `);

  const [archiveContacts] =
    useContraMutation<useNetworkArchiveActionsArchiveContactsMutation>(graphql`
      mutation useNetworkArchiveActionsArchiveContactsMutation(
        $input: ArchiveContactsInput!
      ) {
        archiveContacts(input: $input) {
          errors {
            __typename
            message
          }
          visitor {
            userAccount {
              workspaceForSelectedUserType {
                ... on ClientWorkspace {
                  __typename
                  userLists(first: 200, filter: { groupNid: NETWORK_LIST }) {
                    edges {
                      node {
                        id
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `);

  const [unarchiveContacts] =
    useContraMutation<useNetworkArchiveActionsUnarchiveContactsMutation>(
      graphql`
        mutation useNetworkArchiveActionsUnarchiveContactsMutation(
          $input: UnarchiveContactsInput!
        ) {
          unarchiveContacts(input: $input) {
            errors {
              __typename
              message
            }
          }
        }
      `,
    );

  const [updateListMemberships] =
    useContraMutation<useNetworkArchiveActionsUpdateMembershipsMutation>(
      graphql`
        mutation useNetworkArchiveActionsUpdateMembershipsMutation(
          $input: UpdateUsersMembershipsInUserListsInput!
        ) {
          updateUsersMembershipsInUserLists(input: $input) {
            errors {
              __typename
              message
            }
          }
        }
      `,
    );

  const handleRemoveUsersFromLists = useCallback(
    ({
      allListIds,
      onSuccess,
      userProfileIds,
    }: {
      allListIds: string[];
      onSuccess?: (allListIds: string[]) => void;
      userProfileIds: string[];
    }) => {
      updateListMemberships({
        onCompleted: (errors) => {
          const { apiErrors, hasErrors, payloadErrors } = errors;
          if (hasErrors) {
            if (payloadErrors) {
              captureException(payloadErrors[0]);
            } else if (apiErrors) {
              toast.error(apiErrors[0].message);
            }
          } else {
            onSuccess?.(allListIds);
          }
        },
        onError: (error) => {
          captureException(error);
        },
        variables: {
          input: {
            userListIdsToAddTo: [],
            userListIdsToRemoveFrom: allListIds,
            userProfileIds,
          },
        },
      });
    },
    [toast, updateListMemberships],
  );

  const handleArchiveContact = useCallback(
    (args?: { onSuccess?: (allListIds: string[]) => void }) => {
      archiveContact({
        onCompleted: (errors, response) => {
          const { apiErrors, hasErrors, payloadErrors } = errors;
          if (hasErrors) {
            if (payloadErrors) {
              toast.error('An unexpected error occurred. Please try again.');
              captureException(payloadErrors[0]);
            } else if (apiErrors) {
              toast.error(apiErrors[0].message);
            }
          } else {
            const allListIds =
              response.archiveContact?.visitor?.userAccount
                ?.workspaceForSelectedUserType?.__typename === 'ClientWorkspace'
                ? response.archiveContact?.visitor.userAccount.workspaceForSelectedUserType.userLists.edges.map(
                    (edge) => edge.node.id,
                  )
                : [];

            if (selectedContactsWithUserProfiles.length > 0) {
              handleRemoveUsersFromLists({
                allListIds,
                userProfileIds: [
                  selectedContactsWithUserProfiles[0].userProfileId,
                ],
              });
            }

            args?.onSuccess?.(allListIds);

            if (!skipToast) {
              toast.success(
                reworkedSaveToListsFlow
                  ? `Unsaved ${selectedContacts[0].fullName}`
                  : `Removed ${selectedContacts[0].fullName} from network`,
              );
            }
          }
        },
        onError: (error) => {
          toast.error('An unexpected error occurred. Please try again.');
          captureException(error);
        },
        variables: {
          input: {
            contactId: selectedContacts[0].contactId,
          },
        },
      });
    },
    [
      archiveContact,
      handleRemoveUsersFromLists,
      reworkedSaveToListsFlow,
      selectedContacts,
      selectedContactsWithUserProfiles,
      skipToast,
      toast,
    ],
  );

  const handleUnarchiveContact = useCallback(
    ({ onSuccess }: { onSuccess?: () => void }) => {
      if (selectedContacts[0].contactId) {
        unarchiveContact({
          onCompleted: (errors) => {
            const { apiErrors, hasErrors, payloadErrors } = errors;
            if (hasErrors) {
              if (payloadErrors) {
                toast.error('An unexpected error occurred. Please try again.');
                captureException(payloadErrors[0]);
              } else if (apiErrors) {
                toast.error(apiErrors[0].message);
              }
            } else {
              onSuccess?.();

              if (!skipToast) {
                toast.success(
                  reworkedSaveToListsFlow
                    ? `Saved ${selectedContacts[0].fullName}`
                    : `Contact unarchived`,
                );
              }
            }
          },
          onError: (error) => {
            toast.error('An unexpected error occurred. Please try again.');
            captureException(error);
          },
          variables: {
            input: {
              contactId: selectedContacts[0].contactId,
            },
          },
        });
      }
    },
    [
      reworkedSaveToListsFlow,
      selectedContacts,
      skipToast,
      toast,
      unarchiveContact,
    ],
  );

  const handleArchiveContacts = useCallback(
    ({ onSuccess }: { onSuccess: (allListIds: string[]) => void }) => {
      archiveContacts({
        onCompleted: (errors, response) => {
          const { apiErrors, hasErrors, payloadErrors } = errors;
          if (hasErrors) {
            if (payloadErrors) {
              toast.error('An unexpected error occurred. Please try again.');
              captureException(payloadErrors[0]);
            } else if (apiErrors) {
              toast.error(apiErrors[0].message);
            }
          } else {
            const allListIds =
              response.archiveContacts?.visitor?.userAccount
                ?.workspaceForSelectedUserType?.__typename === 'ClientWorkspace'
                ? response.archiveContacts?.visitor.userAccount.workspaceForSelectedUserType.userLists.edges.map(
                    (edge) => edge.node.id,
                  )
                : [];

            if (selectedContactsWithUserProfiles.length > 0) {
              handleRemoveUsersFromLists({
                allListIds,
                onSuccess,
                userProfileIds: selectedContactsWithUserProfiles.map(
                  (contact) => contact.userProfileId,
                ),
              });
            } else {
              onSuccess(allListIds);
            }

            if (selectedContacts.length === 1) {
              if (!skipToast) {
                toast.success(
                  reworkedSaveToListsFlow
                    ? `Unsaved ${selectedContacts[0].fullName}`
                    : `Removed ${selectedContacts[0].fullName} from network`,
                );
              }
            } else if (!skipToast) {
              toast.success(
                `${selectedContacts.length} contractors were removed from your network`,
              );
            }
          }
        },
        onError: (error) => {
          toast.error('An unexpected error occurred. Please try again.');
          captureException(error);
        },
        variables: {
          input: {
            contactIds: selectedContacts.map((contact) => contact.contactId),
          },
        },
      });
    },
    [
      archiveContacts,
      handleRemoveUsersFromLists,
      reworkedSaveToListsFlow,
      selectedContacts,
      selectedContactsWithUserProfiles,
      skipToast,
      toast,
    ],
  );

  const handleUnarchiveContacts = useCallback(
    ({ onSuccess }: { onSuccess?: () => void }) => {
      unarchiveContacts({
        onCompleted: (errors) => {
          const { apiErrors, hasErrors, payloadErrors } = errors;
          if (hasErrors) {
            if (payloadErrors) {
              toast.error('An unexpected error occurred. Please try again.');
              captureException(payloadErrors[0]);
            } else if (apiErrors) {
              toast.error(apiErrors[0].message);
            }
          } else {
            onSuccess?.();

            if (!skipToast) {
              toast.success(
                reworkedSaveToListsFlow
                  ? `Saved ${selectedContacts.length} contact${selectedContacts.length === 1 ? `` : `s`}`
                  : `Contacts unarchived`,
              );
            }
          }
        },
        onError: (error) => {
          toast.error('An unexpected error occurred. Please try again.');
          captureException(error);
        },
        variables: {
          input: {
            contactIds: selectedContacts.map((contact) => contact.contactId),
          },
        },
      });
    },
    [
      reworkedSaveToListsFlow,
      selectedContacts,
      skipToast,
      toast,
      unarchiveContacts,
    ],
  );

  return {
    archiveContact: handleArchiveContact,
    archiveContacts: handleArchiveContacts,
    unarchiveContact: handleUnarchiveContact,
    unarchiveContacts: handleUnarchiveContacts,
  };
};
